import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Users, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const UserRoleManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUserDetails(response.data);
    } catch (error) {
      toast.error("Failed to fetch user details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleStatusChange = async (userId, isActive) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { is_active: isActive }, { withCredentials: true });
      toast.success(isActive ? "User activated" : "User deactivated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("User deleted");
      fetchUsers();
      setSelectedUser(null);
      setUserDetails(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-user-management">
      <div>
        <h2 className="text-2xl font-serif">User & Role Management v2</h2>
        <p className="text-muted-foreground text-sm">Manage all user accounts, roles, and access. Updated March 2026.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-4 p-4 bg-muted text-sm font-medium">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-4">Actions</div>
          </div>
          <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
            {users.map((user) => (
              <div key={user.user_id} className={`grid grid-cols-12 gap-4 p-4 items-center hover:bg-muted/50 ${selectedUser?.user_id === user.user_id ? 'bg-muted/50' : ''}`}>
                <div className="col-span-4 flex items-center gap-2">
                  <img src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`} alt={user.name} className="w-8 h-8 rounded-full" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
                <div className="col-span-2">
                  <Select value={user.role} onValueChange={(value) => handleRoleChange(user.user_id, value)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="ceo">CEO</SelectItem>
                      <SelectItem value="admin">General Manager (Admin)</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="data_entry">Data Entry</SelectItem>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="constructor">Constructor</SelectItem>
                      <SelectItem value="letting_manager">Letting Manager</SelectItem>
                      <SelectItem value="sales_manager">Sales Manager</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="branch_manager">Branch Manager</SelectItem>
                      <SelectItem value="regional_manager">Regional Manager</SelectItem>
                      <SelectItem value="broker">Broker</SelectItem>
                      <SelectItem value="people_culture">People & Culture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Badge variant={user.is_active !== false ? "default" : "destructive"}>
                    {user.is_active !== false ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="col-span-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setSelectedUser(user); fetchUserDetails(user.user_id); }}>
                    View
                  </Button>
                  <Button size="sm" variant={user.is_active !== false ? "outline" : "default"} onClick={() => handleStatusChange(user.user_id, user.is_active === false)}>
                    {user.is_active !== false ? "Deactivate" : "Activate"}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(user.user_id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-4">
          {selectedUser ? (
            detailsLoading ? (
              <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : userDetails ? (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b border-border">
                  <img src={selectedUser.picture || `https://ui-avatars.com/api/?name=${selectedUser.name}`} alt={selectedUser.name} className="w-16 h-16 rounded-full mx-auto mb-2" />
                  <h3 className="font-medium">{selectedUser.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  <Badge className="mt-2">{selectedUser.role}</Badge>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Properties</span>
                    <span className="font-medium">{userDetails.stats.total_properties}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Leads</span>
                    <span className="font-medium">{userDetails.stats.total_leads}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Deals</span>
                    <span className="font-medium">{userDetails.stats.total_deals}</span>
                  </div>
                </div>
                {userDetails.agent_profile && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Agent Profile</p>
                    <p className="text-sm">{userDetails.agent_profile.bio?.slice(0, 100)}...</p>
                  </div>
                )}
              </div>
            ) : null
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a user to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRoleManager;
